'use client';

import { useEffect, useContext, useCallback, type FC } from 'react';
import { User } from '@/_types/models/user';
import { Firm } from '@/_types/models/firm';
import * as Sentry from '@sentry/nextjs';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import type { SentryUserReporterProps } from './SentryUserReporter.types';

export const SentryUserReporter: FC<SentryUserReporterProps> = () => {
  const { user } = useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);

  const setSentryScope = useCallback(
    ({ user, firm }: { user?: User; firm?: Firm }) => {
      try {
        if (typeof Sentry.setUser !== 'function') {
          console.error('Sentry.setUser is not available');
          return;
        }

        if (typeof Sentry.setTags !== 'function') {
          console.error('Sentry.setTags is not available');
          return;
        }

        Sentry.setUser({
          email: user?.email || '',
          id: user?.id,
          is_advisor: user?.is_advisor,
          lname: user?.lname,
          fname: user?.fname,
          uid: user?.uid,
        });

        Sentry.setTags({
          firm_id: firm?.id,
        });
      } catch (error) {
        console.error('Failed to set Sentry scope', error);
      }
    },
    [],
  );

  useEffect(() => {
    if (!user?.id) {
      setSentryScope({ user: undefined, firm: undefined });
      return;
    }

    setSentryScope({ user, firm });
  }, [setSentryScope, user, firm]);

  return null;
};
